<template>
  <div v-if="isInitiative && npa.npa.status.id === 12" class="mb-6">
    <v-card
      outlined
      class="pa-0"
      style="overflow: hidden; border-left-width: 6px; border-left-style: solid;"
      :style="{ 'border-left-color': getStatusColorHex }"
    >
      <div class="d-flex align-center justify-space-between py-3 px-4 flex-wrap">

        <div class="d-flex align-center mr-4" style="min-width: 200px;">
          <v-avatar :color="statusColor" size="36" class="mr-3">
            <v-icon dark small>{{ getStatusIcon }}</v-icon>
          </v-avatar>

          <div>
            <div class="caption grey--text text--darken-1 line-height-1">
              {{ $t('Народная инициатива') }}
            </div>
            <div class="font-weight-bold d-flex align-center" :class="`${statusColor}--text`">
              {{ npa.npa.initiativeStatus.transName }}

              <v-tooltip bottom v-if="npa.npa.stopCollectionReason">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="grey"
                    class="ml-1"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-information-outline
                  </v-icon>
                </template>
                <span>
                  <strong>{{ $t('Причина остановки:') }}</strong><br>
                  {{ npa.npa.stopCollectionReason }}
                </span>
              </v-tooltip>
            </div>
            <div class="caption grey--text mt-1">
              <v-icon x-small class="mr-1">mdi-eye</v-icon>
              {{ npa.npa.view_number || 0 }} {{ $t('просмотров') }}
            </div>
          </div>
        </div>

        <div class="flex-grow-1 mx-4 d-none d-md-block">
          <div class="d-flex justify-space-between caption font-weight-medium mb-1">
            <span>{{ $t('Собрано подписей') }}</span>
            <span>
              <span :class="`${statusColor}--text text-h6`">{{ npa.npa.votesCount || 0 }}</span>
              <span class="grey--text"> / 10000</span>
            </span>
          </div>
          <v-progress-linear
            :value="(npa.npa.votesCount / 10000) * 100"
            height="6"
            rounded
            :color="statusColor"
            background-color="grey lighten-3"
          ></v-progress-linear>
        </div>

        <div class="d-flex align-center justify-end" style="min-width: 220px;">

          <div v-if="isSignatureActionsVisible" class="mr-2">
            <v-btn
              v-if="canSign"
              color="success"
              @click="openSignDialog"
              :loading="signingLoading"
              rounded
              elevation="2"
              small
            >
              <v-icon left small>mdi-pen</v-icon>
              {{ $t('Поддержать') }}
            </v-btn>

            <v-btn
              v-else-if="alreadySigned"
              color="grey darken-1"
              outlined
              rounded
              small
              @click="openWithdrawDialog"
              :loading="signingLoading"
            >
              {{ $t('Отозвать подпись') }}
              <v-icon right x-small class="ml-1">mdi-close-circle</v-icon>
            </v-btn>

            <span
              v-else-if="!canStopCollection"
              class="caption grey--text d-inline-block text-right"
              style="max-width: 150px; line-height: 1.2;"
            >
              {{ cantSignReason }}
            </span>
          </div>

          <v-chip
            v-else-if="alreadySigned"
            small
            color="grey lighten-3"
            class="mr-md-2"
          >
            <v-icon left small>mdi-check</v-icon>
            {{ $t('Ваш голос учтен') }}
          </v-chip>

          <div v-if="canStopCollection" class="text-sm-right text-center mt-sm-0 mt-2">
            <v-btn
              color="error"
              outlined
              rounded
              small
              @click="stopDialog = true"
            >
              {{ $t('Остановить') }}
            </v-btn>
            <v-btn
              v-if="canAccessSignatureSheet"
              class="ml-md-2 mt-sm-0 mt-1"
              color="info"
              outlined
              rounded
              small
              @click="openSheetDialog"
              :loading="sheetLoading"
            >
              <v-icon left small>mdi-eye</v-icon>
              {{ $t('Эл. подписной лист') }}
            </v-btn>
            <v-btn
              v-if="canAccessSignatureSheet"
              class="ml-md-2 mt-sm-0 mt-1"
              color="primary"
              text
              outlined
              rounded
              small
              @click="downloadSignatures"
              :loading="downloadLoading"
            >
              <v-icon left small>mdi-file-word</v-icon>
              {{ $t('Подписной лист') }}
            </v-btn>
          </div>
          <div v-else-if="canAccessSignatureSheet" class="ml-2">
            <v-btn
              color="info"
              outlined
              rounded
              small
              class="mr-2"
              @click="openSheetDialog"
              :loading="sheetLoading"
            >
              <v-icon left small>mdi-eye</v-icon>
              {{ $t('Эл. подписной лист') }}
            </v-btn>
            <v-btn
              color="primary"
              outlined
              rounded
              small
              @click="downloadSignatures"
              :loading="downloadLoading"
            >
              <v-icon left small>mdi-download</v-icon>
              {{ $t('Скачать итоги') }}
            </v-btn>
          </div>
        </div>
      </div>
    </v-card>

    <v-dialog v-model="stopDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline error--text">
          {{ $t('Остановка сбора подписей') }}
        </v-card-title>
        <v-card-text>
          <p class="body-2">
            {{ $t('Вы собираетесь досрочно остановить сбор подписей. Пожалуйста, укажите причину.') }}
          </p>
          <v-textarea
            v-model="stopReason"
            :label="$t('Причина остановки')"
            outlined
            rows="3"
            hide-details
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="stopDialog = false">{{ $t('Отмена') }}</v-btn>
          <v-btn
            color="error"
            @click="submitStopCollection"
            :loading="stopLoading"
            :disabled="!stopReason.trim()"
          >
            {{ $t('Остановить') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="signDialog" max-width="760px">
      <v-card>
        <v-card-title class="headline">
          {{ signatureDialogTitle }}
        </v-card-title>
        <v-card-text>
          <div class="mb-4 body-2">
            {{ signatureDialogDescription }}
          </div>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="signForm.fio"
                :label="$t('ФИО')"
                outlined
                dense
                readonly
              />
            </v-col>
            <v-col v-if="signatureAction === 'sign'" cols="12" md="6">
              <v-select
                v-model="signForm.region"
                :items="regions"
                :loading="regionsLoading"
                item-text="label"
                item-value="value"
                :label="$t('Область или город областного значения')"
                outlined
                dense
                :rules="[v => !!v || $t('Поле обязательно')]"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="selectedAuthMethod"
                :items="authMethodOptions"
                :loading="authMethodsLoading"
                item-text="text"
                item-value="value"
                :label="$t('Способ получения одноразового кода')"
                outlined
                dense
                :disabled="codeSent"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-btn
                color="primary"
                outlined
                :loading="codeSending"
                :disabled="!selectedAuthMethod"
                @click="sendSignatureCode"
              >
                {{ codeSent ? $t('Отправить код повторно') : $t('Получить код') }}
              </v-btn>
            </v-col>
            <v-col v-if="codeSent" cols="12" md="6">
              <v-text-field
                v-model.trim="otpCode"
                :label="$t('Одноразовый код')"
                inputmode="numeric"
                autocomplete="one-time-code"
                maxlength="32"
                outlined
                dense
              />
            </v-col>
          </v-row>
          <v-alert v-if="codeSent" type="info" text dense class="mb-0">
            {{ $t('Код отправлен. Введите его для формирования электронной подписи ОЭП.') }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeSignatureDialog">{{ $t('Отмена') }}</v-btn>
          <v-btn
            :color="signatureAction === 'sign' ? 'success' : 'warning'"
            :loading="signingLoading"
            :disabled="!canSubmitSignForm"
            @click="confirmSignatureAction"
          >
            {{ signatureAction === 'sign' ? $t('Поддержать') : $t('Отозвать подпись') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="sheetDialog" max-width="230mm" content-class="sheet-preview-dialog">
      <v-card class="sheet-preview-card">
        <v-card-text>
          <div class="sheet-preview-body">
            <div class="sheet-doc-title">ЭЛЕКТРОННЫЙ ПОДПИСНОЙ ЛИСТ</div>

            <table class="sheet-table sheet-table--doc mb-5">
              <thead>
                <tr>
                  <th>Номер свидетельства о<br>регистрации инициативной<br>группы</th>
                  <th>Дата<br>регистрации</th>
                  <th>Наименование органа, выдавшего<br>свидетельство о регистрации</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{ sheetData.registration.reg_number || '' }}</td>
                  <td>{{ sheetData.registration.reg_date || '' }}</td>
                  <td>{{ sheetData.registration.reg_authority || '' }}</td>
                </tr>
              </tbody>
            </table>

            <div class="sheet-paragraph">
              Я, ______________________________ гражданин Кыргызской Республики, авторизованный в Едином портале общественного обсуждения проектов нормативных правовых актов Кыргызской Республики посредством Единой системы идентификации, ознакомившись с текстом проекта закона (в случае внесения проекта закона о назначении референдума с текстом проекта закона о назначении референдума и текста проекта вопроса, выносимого на референдум), подтверждаю поддержку внесения проекта закона в Жогорку Кенеш Кыргызской Республики.
            </div>

            <div class="sheet-line-wrap">
              <div class="sheet-title-on-line">{{ sheetData.project.title }}</div>
              <div class="sheet-line"></div>
              <div class="sheet-line-caption">(наименование проекта закона)</div>
            </div>

            <div class="sheet-line-wrap">
              <div class="sheet-line"></div>
              <div class="sheet-line-caption">(в случае внесения проекта закона о назначении референдума</div>
            </div>

            <div class="sheet-line-wrap mb-2">
              <div class="sheet-line"></div>
              <div class="sheet-line-caption">наименование текста проекта вопроса, выносимого на референдум)</div>
            </div>

            <div class="sheet-paragraph mb-5">{{ sheetData.project.publication_note }}</div>

            <table class="sheet-table sheet-table--doc">
              <thead>
                <tr>
                  <th>№<br>п/п</th>
                  <th>Фамилия<br>имя, отчество</th>
                  <th>Дата<br>рождения</th>
                  <th>Адрес места<br>жительства<br>(область/город)</th>
                  <th>ИНН<br>гражда<br>нина</th>
                  <th>Электрон<br>ная<br>подпись<br>(хеш-<br>значение)</th>
                  <th>Дата,<br>время<br>внесения<br>электрон<br>ной<br>подписи</th>
                  <th>Сведения<br>об отзыве<br>подписи<br>(дата,<br>время)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in sheetData.rows" :key="row.num">
                  <td>{{ row.num }}</td>
                  <td>{{ row.fio }}</td>
                  <td>{{ row.birth_date }}</td>
                  <td>{{ row.region_or_city }}</td>
                  <td>{{ row.inn }}</td>
                  <td>{{ row.signature_hash }}</td>
                  <td>{{ row.signed_at }}</td>
                  <td>{{ row.revoked_at }}</td>
                </tr>
                <tr v-if="!sheetData.rows.length">
                  <td colspan="8"> </td>
                </tr>
              </tbody>
            </table>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="sheetDialog = false">{{ $t('Закрыть') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { Trans } from '@/plugins/Translation'
import axios from 'axios'
export default {
  name: 'NpaInitiative',
  data() {
    return {
      baseUrl: process.env.VUE_APP_BACK_URL,
      alreadySigned: false,
      signingLoading: false,
      stopDialog: false,
      signDialog: false,
      sheetDialog: false,
      sheetLoading: false,
      regionsLoading: false,
      stopReason: '',
      stopLoading: false,
      downloadLoading: false,
      signForm: {
        fio: '',
        region: null
      },
      signatureAction: 'sign',
      authMethods: [],
      selectedAuthMethod: null,
      authMethodsLoading: false,
      codeSending: false,
      codeSent: false,
      otpCode: '',
      sheetData: {
        registration: {
          reg_number: '',
          reg_date: '',
          reg_authority: ''
        },
        project: {
          title: '',
          publication_note: ''
        },
        rows: []
      },
      regions: [
        // заполняется из справочника refRegions с фильтром по правилам задачи
      ]
    }
  },
  props: {
    npa: {
      type: Object,
      required: true
    }
  },
  mounted() {
    this.alreadySigned = this.npa.isSignedByUser
  },
  computed: {
    user() {
      return this.$store.state.getUser.currentUser
    },
    isInitiative() {
      return this.npa.npa && this.npa.npa.view.id === 4;
    },

    canSign() {
      if (!this.isSignatureActionsEnabled) return false;

      return this.user
        && this.isEsiaOrLocalBypassUser
        && !this.alreadySigned
        ;
    },
    isSignatureActionsEnabled() {
      return process.env.VUE_APP_INITIATIVE_SIGNATURE_ENABLED === 'true';
    },
    localSignatureMode() {
      return process.env.VUE_APP_LOCAL_SIGNATURE_MODE === 'true';
    },
    isEsiaOrLocalBypassUser() {
      return this.user?.auth_provider === 'esia' || this.localSignatureMode;
    },
    canSubmitSignForm() {
      const hasRequiredRegion = this.signatureAction !== 'sign' || !!this.signForm.region;
      return hasRequiredRegion
        && !!this.selectedAuthMethod
        && this.codeSent
        && !!this.otpCode;
    },
    authMethodOptions() {
      return this.authMethods.map(method => ({
        value: method,
        text: method === 'sms' ? this.$t('SMS') : this.$t('Электронная почта')
      }));
    },
    signatureDialogTitle() {
      return this.signatureAction === 'sign'
        ? this.$t('Поддержать народную инициативу')
        : this.$t('Отозвать подпись');
    },
    signatureDialogDescription() {
      return this.signatureAction === 'sign'
        ? this.$t('Подтвердите данные и действие одноразовым кодом ОЭП')
        : this.$t('Отзыв также необходимо подтвердить одноразовым кодом ОЭП');
    },
    isSignatureActionsVisible() {
      return this.isSignatureActionsEnabled && !this.isClosed && !this.isOnIntroduction;
    },

    cantSignReason() {
      if (!this.user) return this.$t('Для подписи нужно войти в систему');
      if (!this.isEsiaOrLocalBypassUser) return this.$t('Для подписи нужен вход через ЕСИ Тундук');
      if (this.alreadySigned) return this.$t('Вы уже подписали');
      return '';
    },
    isClosed() {
      return this.npa.npa?.initiativeStatus?.id === 3;
    },
    isOnIntroduction() {
      return this.npa.npa?.initiativeStatus?.id === 1;
    },
    canStopCollection() {
      if (!this.user) return false;
      const statusId = this.npa.npa?.initiativeStatus?.id;

      if (statusId !== 2) return false;

      return this.npa.npa?.author?.id === this.user.id;
    },
    statusColor() {
      const statusId = this.npa.npa?.initiativeStatus?.id;
      const votes = this.npa.npa?.votesCount || 0;

      if (statusId === 2 || (statusId === 3 && votes >= 10000)) {
        return 'success';
      }

      if (statusId === 3) {
        return 'error';
      }

      return 'info';
    },

    getStatusColorHex() {
      const map = {
        'success': '#4CAF50',
        'error': '#FF5252',
        'info': '#2196F3'
      };
      return map[this.statusColor] || '#2196F3';
    },

    getStatusIcon() {
      const statusId = this.npa.npa?.initiativeStatus?.id;
      const votes = this.npa.npa?.votesCount || 0;

      if (statusId === 2) return 'mdi-account-group';

      if (statusId === 3) {
        if (votes >= 10000) return 'mdi-check-decagram'; // Или mdi-trophy

        return 'mdi-lock';
      }

      return 'mdi-eye';
    },
    canAccessSignatureSheet() {
      if (!this.user) return false;
      return this.npa.npa?.author?.id === this.user.id
        && this.user.roleInitiativeGroupUser === true
        && !!this.user.initiativeGroup;
    }
  },
  methods: {
    openSignDialog() {
      if (!this.isSignatureActionsEnabled || !this.canSign) return;

      this.prepareSignatureDialog('sign');
    },
    openWithdrawDialog() {
      if (!this.isSignatureActionsEnabled || !this.alreadySigned) return;

      if (!confirm(this.$t('Вы действительно хотите отозвать свою подпись?'))) {
        return;
      }

      this.prepareSignatureDialog('withdraw');
    },
    prepareSignatureDialog(action) {
      this.signatureAction = action;
      this.signForm.fio = this.getFioForSignForm();
      this.signForm.region = this.signForm.region || null;
      this.authMethods = [];
      this.selectedAuthMethod = null;
      this.codeSent = false;
      this.otpCode = '';

      const regionsRequest = action === 'sign' ? this.fetchSignRegions() : Promise.resolve();
      Promise.all([regionsRequest, this.fetchAuthMethods(action)]).finally(() => {
        this.signDialog = true;
      });
    },
    closeSignatureDialog() {
      this.signDialog = false;
      this.codeSent = false;
      this.otpCode = '';
    },
    getJwtPayload() {
      try {
        const token = localStorage.getItem('token');
        if (!token || token.split('.').length < 2) return null;
        const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const normalized = payload + '='.repeat((4 - payload.length % 4) % 4);
        return JSON.parse(atob(normalized));
      } catch (e) {
        return null;
      }
    },
    getFioForSignForm() {
      const payload = this.getJwtPayload() || {};
      const tokenFio = payload.fio || payload.full_name || payload.name || '';
      if (tokenFio) return tokenFio;
      if (this.user?.fio) return this.user.fio;

      const firstName = this.user?.first_name || this.user?.firstName || '';
      const secondName = this.user?.second_name || this.user?.secondName || '';
      const lastName = this.user?.last_name || this.user?.lastName || '';

      return [firstName, secondName, lastName].filter(Boolean).join(' ').trim();
    },
    fetchAuthMethods(action) {
      this.authMethodsLoading = true;
      return axios
        .get(`${this.baseUrl}${Trans.currentLocale}/api/initiative/${this.npa.npa.id}/signature/auth-methods`, {
          params: { action }
        })
        .then(({ data }) => {
          this.authMethods = Array.isArray(data?.methods) ? data.methods : [];
          this.selectedAuthMethod = this.authMethods.length === 1 ? this.authMethods[0] : null;
          if (!this.authMethods.length) {
            throw new Error(this.$t('Нет доступного способа получения одноразового кода'));
          }
        })
        .catch(error => {
          const message = error.response?.data?.error
            || error.message
            || this.$t('Не удалось получить способы подтверждения ОЭП');
          this.$noty.error(message, {
            killer: true,
            timeout: 1800,
            layout: 'topRight'
          });
        })
        .finally(() => {
          this.authMethodsLoading = false;
        });
    },
    sendSignatureCode() {
      if (!this.selectedAuthMethod) return;

      this.codeSending = true;
      axios
        .post(`${this.baseUrl}${Trans.currentLocale}/api/initiative/${this.npa.npa.id}/signature/send-code`, {
          action: this.signatureAction,
          method: this.selectedAuthMethod
        })
        .then(() => {
          this.codeSent = true;
          this.otpCode = '';
          this.$noty.success(this.$t('Одноразовый код отправлен'), {
            killer: true,
            timeout: 1200,
            layout: 'topRight'
          });
        })
        .catch(error => {
          const message = error.response?.data?.error || this.$t('Не удалось отправить одноразовый код');
          this.$noty.error(message, {
            killer: true,
            timeout: 1800,
            layout: 'topRight'
          });
        })
        .finally(() => {
          this.codeSending = false;
        });
    },
    fetchSignRegions() {
      if (this.regions.length) return Promise.resolve();

      this.regionsLoading = true;
      return axios
        .get(`${this.baseUrl}${Trans.currentLocale}/api/npa/references`, { params: { ref: 'refRegions' } })
        .then(({ data }) => {
          const normalize = (s) => (s || '')
            .toString()
            .toLowerCase()
            .replace(/\./g, '')
            .replace(/ё/g, 'е')
            .replace(/\s+/g, ' ')
            .trim();

          const allowedNames = [
            'чуйская область',
            'таласская область',
            'иссык-кульская область',
            'нарынская область',
            'джалал-абадская область',
            'ошская область',
            'баткенская область',
            'бишкек',
            'ош'
          ];

          const allowed = new Set(allowedNames.map(normalize));

          this.regions = (Array.isArray(data) ? data : [])
            .map((item) => {
              const label = item.transName || item.name || '';
              return {
                value: label,
                label
              };
            })
            .filter((item) => {
              const n = normalize(item.label);
              if (allowed.has(n)) return true;
              if (n.endsWith(' область') && allowed.has(n)) return true;
              return false;
            })
            .sort((a, b) => a.label.localeCompare(b.label, 'ru'));
        })
        .catch(() => {
          this.regions = [];
          this.$noty.error(this.$t('Не удалось загрузить справочник регионов'), {
            killer: true,
            timeout: 1200,
            layout: 'topRight'
          });
        })
        .finally(() => {
          this.regionsLoading = false;
        });
    },
    confirmSignatureAction() {
      if (!this.isSignatureActionsEnabled) return;

      this.signingLoading = true;
      const isSupport = this.signatureAction === 'sign';
      const endpoint = isSupport ? 'sign' : 'withdraw';

      axios.post(`${this.baseUrl}${Trans.currentLocale}/api/initiative/${this.npa.npa.id}/${endpoint}`, {
        code: this.otpCode,
        method: this.selectedAuthMethod,
        region: isSupport ? this.signForm.region : undefined
      })
        .then(response => {
          this.$noty.success(
            isSupport
              ? this.$t('Вы успешно поддержали инициативу')
              : this.$t('Подпись отозвана'),
            {
            killer: true,
            timeout: 1000,
            layout: 'topRight'
            }
          );
          this.alreadySigned = isSupport;
          this.closeSignatureDialog();
          if (response.data?.votes_count !== undefined) {
            this.npa.npa.votesCount = response.data.votes_count;
          }
        })
        .catch(error => {
          const msg = error.response?.data?.error
            || (isSupport ? 'Ошибка при подписании' : 'Ошибка при отзыве подписи');
          this.$noty.error(this.$t(msg), {
            killer: true,
            timeout: 1800,
            layout: 'topRight'
          });
        })
        .finally(() => {
          this.signingLoading = false;
        });
    },
    openSheetDialog() {
      this.sheetLoading = true;
      axios.get(`${this.baseUrl}${Trans.currentLocale}/api/initiative/${this.npa.npa.id}/sheet-data`)
        .then((response) => {
          this.sheetData = response?.data?.sheet || this.sheetData;
          this.sheetDialog = true;
        })
        .catch(() => {
          this.$noty.error(this.$t('Не удалось загрузить подписной лист'), {
            killer: true,
            timeout: 1200,
            layout: 'topRight'
          });
        })
        .finally(() => {
          this.sheetLoading = false;
        });
    },

    submitStopCollection() {
      if (!this.stopReason.trim()) return;

      if (!confirm(this.$t('Вы уверены? Сбор подписей будет закрыт.'))) {
        return;
      }

      this.stopLoading = true;

      axios.post(`${this.baseUrl}${Trans.currentLocale}/api/initiative/${this.npa.npa.id}/stop`, {
        reason: this.stopReason
      })
        .then(response => {
          this.$noty.success(this.$t('Сбор подписей успешно остановлен'), {
            killer: true,
            timeout: 1000,
            layout: 'topRight'
          });
          this.stopDialog = false;
          this.stopReason = '';

          this.$emit('fetch-data');
        })
        .catch(error => {
          const msg = error.response?.data?.error || 'Ошибка при остановке сбора';
          this.$noty.error(this.$t(msg), {
            killer: true,
            timeout: 1000,
            layout: 'topRight'
          });
        })
        .finally(() => {
          this.stopLoading = false;
        });
    },
    downloadSignatures() {
      this.downloadLoading = true;
      const url = `${this.baseUrl}${Trans.currentLocale}/api/initiative/${this.npa.npa.id}/export-signatures`;

      axios({
        url: url,
        method: 'GET',
        responseType: 'blob', // Важно для скачивания файлов
      }).then((response) => {
        // Создаем ссылку для скачивания
        const href = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', `signatures_${this.npa.npa.id}.docx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      }).catch((error) => {
        this.$message.error(this.$t('Ошибка при скачивании файла'));
      }).finally(() => {
        this.downloadLoading = false;
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.line-height-1 {
  line-height: 1;
}
.sheet-preview-card {
  background: #fff;
}
.sheet-preview-dialog {
  width: 230mm;
  max-width: calc(100vw - 32px);
}
.sheet-preview-title {
  font-size: 20px;
  font-weight: 500;
}
.sheet-preview-body {
  width: 210mm; /* точная ширина A4 */
  min-height: 297mm; /* точная высота A4 */
  max-width: calc(100vw - 120px);
  margin: 0 auto;
  padding: 12mm 12mm 10mm 12mm; /* поля, близкие к DOCX */
  font-family: "Times New Roman", Times, serif;
  color: #000;
  box-sizing: border-box;
  background: #fff;
  overflow-y: auto;
}
.sheet-doc-title {
  text-align: center;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.08;
  margin: 4px 0 16px;
  letter-spacing: 0;
}
.sheet-paragraph {
  font-size: 16px;
  line-height: 1.18;
  text-align: justify;
  margin: 8px 0;
}
.sheet-line-wrap {
  margin: 4px 0 10px;
}
.sheet-title-on-line {
  font-size: 19px;
  font-weight: 700;
  text-decoration: underline;
  line-height: 1.1;
}
.sheet-line {
  border-bottom: 1px solid #000;
  height: 8px;
}
.sheet-line-caption {
  text-align: center;
  font-size: 15px;
  line-height: 1.1;
}
.sheet-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}
.sheet-table--doc {
  font-size: 16px;
  line-height: 1.15;
}
.sheet-table th,
.sheet-table td {
  border: 1px solid #000;
  padding: 8px 8px;
  vertical-align: top;
  word-break: break-word;
}
.sheet-table--doc th {
  font-weight: 700;
  text-align: center;
}
.sheet-table--doc td {
  text-align: center;
}
.sheet-table--doc th:nth-child(1) { width: 4%; }
.sheet-table--doc th:nth-child(2) { width: 17%; }
.sheet-table--doc th:nth-child(3) { width: 10%; }
.sheet-table--doc th:nth-child(4) { width: 18%; }
.sheet-table--doc th:nth-child(5) { width: 11%; }
.sheet-table--doc th:nth-child(6) { width: 16%; }
.sheet-table--doc th:nth-child(7) { width: 14%; }
.sheet-table--doc th:nth-child(8) { width: 10%; }
.sheet-table--doc td:nth-child(2),
.sheet-table--doc td:nth-child(4) {
  text-align: center;
}
</style>
